import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageIcon from "@mui/icons-material/Image";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import CategoryIcon from "@mui/icons-material/Category";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8" },
    secondary: { main: "#C4922A" },
    error: { main: "#DC2626" },
    background: { default: "#F0F2F8", paper: "#FFFFFF" },
    text: { primary: "#0F1117", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    h2: { fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const cardEntrance = keyframes`
  from { opacity: 0; transform: translateY(14px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const skeletonPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;


// ─── Styled Components ────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background: "linear-gradient(145deg, #EEF2FF 0%, #F0F2F8 40%, #E8EDF8 100%)",
  padding: "28px 20px 60px",
  fontFamily: "'Sora', sans-serif",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -120,
    right: -120,
    width: 480,
    height: 480,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const ContentBox = styled(Box)({ maxWidth: 900, margin: "0 auto" });

const SectionCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "delay",
})(({ theme, delay }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.04)",
  padding: "24px 28px",
  marginBottom: 20,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(37,99,235,0.06), 0 12px 32px rgba(0,0,0,0.06)",
  },
}));

const CardLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.9px",
  color: theme.palette.text.disabled,
  marginBottom: 18,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "&::before": {
    content: '""',
    display: "inline-block",
    width: 3,
    height: 14,
    background: theme.palette.primary.main,
    borderRadius: 2,
  },
}));

const BackBtn = styled(Button)(({ theme }) => ({
  borderRadius: 40,
  padding: "7px 18px 7px 12px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha("#000", 0.12)}`,
  background: "#fff",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transform: "translateX(-2px)",
  },
}));

const PrimaryBtn = styled(Button)(() => ({
  background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
  color: "#fff",
  borderRadius: 10,
  padding: "11px 28px",
  fontSize: 14,
  fontWeight: 700,
  textTransform: "none",
  fontFamily: "'Sora', sans-serif",
  boxShadow: "0 4px 14px rgba(37,99,235,0.28)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #1D4ED8 0%, #1E3A8A 100%)",
    boxShadow: "0 6px 20px rgba(37,99,235,0.38)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": {
    background: "#E5E7EB",
    color: "#9CA3AF",
    boxShadow: "none",
  },
}));

const StyledInput = styled("input")(() => ({
  width: "100%",
  fontFamily: "'Sora', sans-serif",
  fontSize: 14,
  color: "#0F1117",
  background: alpha("#F0F2F8", 0.8),
  border: `1px solid ${alpha("#000", 0.1)}`,
  borderRadius: 10,
  padding: "11px 14px",
  outline: "none",
  boxSizing: "border-box",
  transition: "all 0.2s ease",
  "&:focus": {
    borderColor: "#2563EB",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(37,99,235,0.08)",
  },
  "&::placeholder": { color: "#9CA3AF" },
}));

const StyledTextArea = styled("textarea")(() => ({
  width: "100%",
  fontFamily: "'Sora', sans-serif",
  fontSize: 14,
  color: "#0F1117",
  background: alpha("#F0F2F8", 0.8),
  border: `1px solid ${alpha("#000", 0.1)}`,
  borderRadius: 10,
  padding: "12px 14px",
  outline: "none",
  boxSizing: "border-box",
  resize: "vertical",
  minHeight: 100,
  lineHeight: 1.65,
  transition: "all 0.2s ease",
  "&:focus": {
    borderColor: "#2563EB",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(37,99,235,0.08)",
  },
  "&::placeholder": { color: "#9CA3AF" },
}));

const StyledSelect = styled("select")(() => ({
  width: "100%",
  fontFamily: "'Sora', sans-serif",
  fontSize: 14,
  color: "#0F1117",
  background: alpha("#F0F2F8", 0.8),
  border: `1px solid ${alpha("#000", 0.1)}`,
  borderRadius: 10,
  padding: "11px 14px",
  outline: "none",
  boxSizing: "border-box",
  appearance: "none",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:focus": {
    borderColor: "#2563EB",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(37,99,235,0.08)",
  },
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.6px",
  textTransform: "uppercase",
  color: "#9CA3AF",
  marginBottom: 6,
  fontFamily: "'Sora', sans-serif",
}));

const NewsCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "itemdelay",
})(({ theme, itemdelay }) => ({
  borderRadius: 14,
  border: `1px solid ${alpha("#000", 0.07)}`,
  boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.03)",
  overflow: "hidden",
  display: "flex",
  marginBottom: 12,
  animation: `${cardEntrance} 0.4s ease both`,
  animationDelay: `${itemdelay ?? 0}ms`,
  transition: "all 0.22s ease",
  "&:last-child": { marginBottom: 0 },
  "&:hover": {
    boxShadow: "0 4px 16px rgba(37,99,235,0.08), 0 8px 24px rgba(0,0,0,0.05)",
    transform: "translateY(-1px)",
    borderColor: alpha("#2563EB", 0.18),
  },
}));

const NewsAccent = styled(Box)(({ theme }) => ({
  width: 4,
  flexShrink: 0,
  background: "linear-gradient(180deg, #2563EB 0%, #60A5FA 100%)",
}));

const ActionBtn = styled(Button, {
  shouldForwardProp: (p) => p !== "btncolor",
})(({ theme, btncolor }) => {
  const map = {
    edit: { bg: alpha("#C4922A", 0.1), color: "#C4922A", hbg: "#C4922A" },
    delete: { bg: alpha("#DC2626", 0.08), color: "#DC2626", hbg: "#DC2626" },
  };
  const s = map[btncolor] || map.edit;
  return {
    minWidth: "unset",
    padding: "5px 12px",
    borderRadius: 7,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "none",
    background: s.bg,
    color: s.color,
    fontFamily: "'Sora', sans-serif",
    transition: "all 0.18s ease",
    "&:hover": {
      background: s.hbg,
      color: "#fff",
      transform: "translateY(-1px)",
      boxShadow: `0 4px 10px ${alpha(s.hbg, 0.3)}`,
    },
  };
});

const ImageUploadZone = styled(Box, {
  shouldForwardProp: (p) => p !== "hasfile",
})(({ hasfile }) => ({
  border: `2px dashed ${hasfile ? "#059669" : alpha("#2563EB", 0.22)}`,
  borderRadius: 12,
  padding: "16px",
  textAlign: "center",
  cursor: "pointer",
  background: hasfile ? alpha("#059669", 0.03) : alpha("#F0F2F8", 0.7),
  transition: "all 0.25s ease",
  "&:hover": {
    borderColor: hasfile ? "#059669" : "#2563EB",
    background: hasfile ? alpha("#059669", 0.05) : alpha("#2563EB", 0.03),
  },
}));

const SkeletonCard = styled(Box)(() => ({
  borderRadius: 14,
  border: `1px solid ${alpha("#000", 0.06)}`,
  overflow: "hidden",
  display: "flex",
  marginBottom: 12,
  background: "#fff",
  animation: `${skeletonPulse} 1.5s ease-in-out infinite`,
}));

const SkeletonBlock = ({ w = "100%", h = 14, radius = 6 }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: radius,
      background: alpha("#000", 0.07),
      flexShrink: 0,
    }}
  />
);

const CatNavBtn = styled(Button)(({ theme }) => ({
  borderRadius: 10,
  padding: "9px 18px",
  fontSize: 13,
  fontWeight: 700,
  textTransform: "none",
  border: `1px solid ${alpha("#000", 0.12)}`,
  color: "text.secondary",
  background: "#fff",
  fontFamily: "'Sora', sans-serif",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha("#2563EB", 0.05),
    borderColor: alpha("#2563EB", 0.3),
    color: "#2563EB",
  },
}));

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminNewsListPage = () => {
  const { countryId } = useParams();
  const navigate = useNavigate();

  const [newsList, setNewsList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [imageUri, setImageUri] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const fetchNews = useCallback(async () => {
    setFetching(true);
    try {
      const res = await axios.get(`${API_URL}/admin/news/${countryId}`);
      setNewsList(res.data);
    } catch (err) {
      console.error("Error fetching news:", err);
    } finally {
      setFetching(false);
    }
  }, [countryId]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/newscategory/`);
      setCategories(res.data);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  }, []);

  useEffect(() => {
    fetchNews();
    fetchCategories();
  }, [fetchNews, fetchCategories]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageUri(e);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const createNews = async () => {
    if (!title || !description || !category) {
      alert("All fields are required.");
      return;
    }
    setLoading(true);
    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    formData.append("category", category);
    if (imageUri) formData.append("news_img", imageUri.target.files[0]);
    try {
      const res = await axios.post(
        `${API_URL}/admin/news/${countryId}/`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setNewsList([...newsList, res.data]);
      setTitle("");
      setDescription("");
      setCategory("");
      setImageUri(null);
      setImagePreview(null);
    } catch (err) {
      console.error("Error creating news:", err);
    } finally {
      setLoading(false);
    }
  };

  const editNews = (id) => navigate(`/admin-edit-news/${id}`);

  const deleteNews = async (id) => {
    if (window.confirm("Are you sure you want to delete this news item?")) {
      try {
        await axios.delete(`${API_URL}/admin/new/${id}/`);
        setNewsList(newsList.filter((n) => n.id !== id));
      } catch (err) {
        console.error("Error deleting news:", err);
      }
    }
  };

  const getCategoryNameById = (id) => {
    const cat = categories.find((c) => c.id === id);
    return cat ? cat.name : "Unknown";
  };

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap"
        rel="stylesheet"
      />

      <PageWrapper>
        <ContentBox>
          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 4,
              animation: `${fadeUp} 0.4s ease both`,
              flexWrap: "wrap",
            }}
          >
            <BackBtn
              startIcon={<ArrowBackIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(-1)}
            >
              Back
            </BackBtn>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 22, sm: 28 },
                  color: "text.primary",
                  lineHeight: 1.1,
                }}
              >
                News Manager
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.disabled",
                  mt: 0.3,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Country ID:{" "}
                <strong style={{ color: "#4B5563" }}>{countryId}</strong>
              </Typography>
            </Box>
            <Box
              sx={{ ml: "auto", display: "flex", gap: 1.5, flexWrap: "wrap" }}
            >
              <CatNavBtn
                startIcon={
                  <CategoryIcon sx={{ fontSize: "15px !important" }} />
                }
                onClick={() => navigate("/admin-news-categories")}
              >
                Categories
              </CatNavBtn>
              <Chip
                label={`${newsList.length} article${
                  newsList.length !== 1 ? "s" : ""
                }`}
                size="small"
                sx={{
                  fontFamily: "'Sora', sans-serif",
                  fontWeight: 700,
                  fontSize: 11,
                  background: alpha("#2563EB", 0.1),
                  color: "#2563EB",
                  border: `1px solid ${alpha("#2563EB", 0.2)}`,
                }}
              />
            </Box>
          </Box>

          {/* ── Create Form Card ── */}
          <SectionCard delay={50}>
            <CardLabel>Create News Article</CardLabel>

            <Box sx={{ mb: 2 }}>
              <FieldLabel>Title *</FieldLabel>
              <StyledInput
                placeholder="Enter news title…"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <FieldLabel>Description *</FieldLabel>
              <StyledTextArea
                placeholder="Enter description…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Box>

            <Box sx={{ mb: 2.5, maxWidth: 280 }}>
              <FieldLabel>Category *</FieldLabel>
              <Box sx={{ position: "relative" }}>
                <CategoryIcon
                  sx={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: 15,
                    color: "text.disabled",
                    pointerEvents: "none",
                  }}
                />
                <StyledSelect
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  style={{ paddingLeft: 36 }}
                >
                  <option value="">Select category…</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </StyledSelect>
                <ArrowForwardIosIcon
                  sx={{
                    position: "absolute",
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%) rotate(90deg)",
                    fontSize: 11,
                    color: "text.disabled",
                    pointerEvents: "none",
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <FieldLabel>Cover Image (optional)</FieldLabel>
              <ImageUploadZone
                hasfile={imagePreview ? 1 : 0}
                onClick={() =>
                  document.getElementById("news-img-input").click()
                }
              >
                <input
                  id="news-img-input"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleImageUpload}
                />
                {imagePreview ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.8,
                    }}
                  >
                    <Box
                      component="img"
                      src={imagePreview}
                      alt="Preview"
                      sx={{
                        maxHeight: 140,
                        maxWidth: "100%",
                        borderRadius: 2,
                        display: "block",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "text.disabled",
                        fontFamily: "'Sora', sans-serif",
                        mt: 0.5,
                      }}
                    >
                      Click to change image
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 3,
                        background: alpha("#2563EB", 0.08),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 0.5,
                      }}
                    >
                      <ImageIcon
                        sx={{ fontSize: 22, color: "#2563EB", opacity: 0.7 }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "text.primary",
                        fontFamily: "'Sora', sans-serif",
                      }}
                    >
                      Upload cover image
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "text.disabled",
                        fontFamily: "'Sora', sans-serif",
                      }}
                    >
                      PNG, JPG · max 10 MB
                    </Typography>
                  </Box>
                )}
              </ImageUploadZone>
            </Box>

            <PrimaryBtn
              startIcon={<AddIcon sx={{ fontSize: "16px !important" }} />}
              disabled={loading}
              onClick={createNews}
            >
              {loading ? "Creating…" : "Create Article"}
            </PrimaryBtn>
          </SectionCard>

          {/* ── News List Card ── */}
          <SectionCard delay={120}>
            <CardLabel>All Articles</CardLabel>

            {/* Skeleton */}
            {fetching ? (
              <Box>
                {[...Array(4)].map((_, i) => (
                  <SkeletonCard key={i} sx={{ animationDelay: `${i * 80}ms` }}>
                    <Box
                      sx={{
                        width: 4,
                        background: alpha("#2563EB", 0.15),
                        flexShrink: 0,
                      }}
                    />
                    <Box
                      sx={{
                        flex: 1,
                        padding: "16px 18px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <SkeletonBlock w="50%" h={14} />
                      <SkeletonBlock w="30%" h={11} />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        padding: "16px 18px",
                        alignItems: "center",
                      }}
                    >
                      <SkeletonBlock w={60} h={28} radius={7} />
                      <SkeletonBlock w={66} h={28} radius={7} />
                    </Box>
                  </SkeletonCard>
                ))}
              </Box>
            ) : newsList.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 3,
                    background: alpha("#2563EB", 0.07),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <NewspaperIcon
                    sx={{ fontSize: 26, color: "primary.main", opacity: 0.45 }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 600,
                  }}
                >
                  No articles yet
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.4,
                  }}
                >
                  Use the form above to publish your first article
                </Typography>
              </Box>
            ) : (
              newsList.map((item, i) => (
                <NewsCard key={item.id} itemdelay={i * 55}>
                  <NewsAccent />
                  <Box sx={{ flex: 1, padding: "14px 18px", minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "text.primary",
                        fontFamily: "'DM Serif Display', serif",
                        lineHeight: 1.25,
                        mb: 0.5,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Chip
                      label={getCategoryNameById(item.category)}
                      size="small"
                      icon={
                        <CategoryIcon sx={{ fontSize: "10px !important" }} />
                      }
                      sx={{
                        fontFamily: "'Sora', sans-serif",
                        fontWeight: 700,
                        fontSize: 10,
                        background: alpha("#2563EB", 0.07),
                        color: "#2563EB",
                        border: `1px solid ${alpha("#2563EB", 0.18)}`,
                        "& .MuiChip-icon": { color: "#2563EB" },
                      }}
                    />
                  </Box>
                  <Stack
                    direction="row"
                    spacing={0.8}
                    sx={{
                      padding: "14px 18px",
                      flexShrink: 0,
                      alignItems: "center",
                    }}
                  >
                    <Tooltip title="Edit article" arrow>
                      <ActionBtn
                        btncolor="edit"
                        onClick={() => editNews(item.id)}
                      >
                        <EditIcon sx={{ fontSize: 13, mr: 0.4 }} />
                        Edit
                      </ActionBtn>
                    </Tooltip>
                    <Tooltip title="Delete article" arrow>
                      <ActionBtn
                        btncolor="delete"
                        onClick={() => deleteNews(item.id)}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 13, mr: 0.4 }} />
                        Delete
                      </ActionBtn>
                    </Tooltip>
                  </Stack>
                </NewsCard>
              ))
            )}
          </SectionCard>
        </ContentBox>
      </PageWrapper>
    </ThemeProvider>
  );
};

export default AdminNewsListPage;
